<template>
  <modal 
    name="LayersOrderModal" 
    :width="'90%'" 
    :max-width="800"
    :height="'auto'" 
    :adaptive="true" 
    :scrollable="true"
    class="premium-modal"
  >
    <div class="modal-content">
      <div class="modal-header">
        <h2>Orden de Capas Existentes</h2>
        <button class="close-button" @click="$modal.hide('LayersOrderModal')" title="Cerrar">&times;</button>
      </div>

      <div class="modal-body">
        <!-- Barra de búsqueda y filtros -->
        <div class="filters-container">
          <div class="search-wrapper">
            <i class="search-icon">🔍</i>
            <input 
              type="text" 
              v-model="searchQuery" 
              placeholder="Buscar capa por nombre..." 
              class="search-input"
            />
          </div>
          
          <div class="order-filter-wrapper">
            <label for="order-filter">Filtrar por Orden:</label>
            <select id="order-filter" v-model="selectedOrder" class="order-select">
              <option value="all">Todos los órdenes</option>
              <option v-for="order in availableOrders" :key="order" :value="order">
                Orden {{ order }}
              </option>
            </select>
          </div>
        </div>

        <div v-if="loading" class="loading-state">
          <div class="spinner"></div>
          <p>Cargando capas...</p>
        </div>
        <div v-else-if="filteredLayers.length === 0" class="empty-state">
          <p>{{ (searchQuery || selectedOrder !== 'all') ? 'No se encontraron capas con los filtros aplicados.' : 'No hay capas registradas.' }}</p>
        </div>
        <div v-else>
          <div v-for="group in groupedByOrder" :key="group.order" class="order-group">
            <div class="group-header" @click="toggleGroup(group.order)" :class="{ 'collapsed': isCollapsed(group.order) }">
              <div class="group-header-left">
                <span class="order-badge"># {{ group.order }}</span>
                <span class="group-title">Capas con Orden {{ group.order }}</span>
              </div>
              <div class="group-header-right">
                <button 
                  class="btn-select-order" 
                  @click.stop="selectOrder(group.order)"
                  title="Usar este número de orden"
                >
                  Seleccionar
                </button>
                <span class="group-count">{{ group.layers.length }} {{ group.layers.length === 1 ? 'capa' : 'capas' }}</span>
                <span class="collapse-icon">{{ isCollapsed(group.order) ? '▼' : '▲' }}</span>
              </div>
            </div>

            <transition name="fade">
              <div v-if="!isCollapsed(group.order)" class="group-content">
                <table class="layers-table">
                  <thead>
                    <tr>
                      <th>Nombre de Capa</th>
                      <th>Categoría</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="layer in group.layers" :key="layer.id">
                      <td class="layer-name">{{ layer.name }}</td>
                      <td class="category-cell">
                        <span class="category-tag">{{ getCategoryName(layer.id_category) }}</span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </transition>
          </div>
        </div>
      </div>

      <div class="modal-footer">
        <button class="btn btn-close" @click="$modal.hide('LayersOrderModal')">Cerrar</button>
      </div>
    </div>
  </modal>
</template>

<script>
import { restLayer } from "@/components/mixin/restLayer.js";

export default {
  name: "LayersOrderModal",
  mixins: [restLayer],
  props: {
    categories: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      loading: false,
      searchQuery: "",
      selectedOrder: "all",
      collapsedGroups: {} // Tracks which order groups are collapsed
    };
  },
  computed: {
    availableOrders() {
      if (!this.layers) return [];
      const orders = this.layers.map(l => l.orden || "0");
      return [...new Set(orders)].sort((a, b) => parseInt(a) - parseInt(b));
    },

    filteredLayers() {
      if (!this.layers) return [];
      
      let layers = this.layers;

      // Filter by name
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        layers = layers.filter(layer => 
          layer.name.toLowerCase().includes(query)
        );
      }

      // Filter by specific order
      if (this.selectedOrder !== "all") {
        layers = layers.filter(layer => 
          (layer.orden || "0") == this.selectedOrder
        );
      }

      return layers;
    },

    groupedByOrder() {
      const groups = {};

      this.filteredLayers.forEach(layer => {
        const order = layer.orden || "0";
        if (!groups[order]) {
          groups[order] = {
            order: order,
            layers: []
          };
        }
        groups[order].layers.push(layer);
      });

      // Sort groups by numerical order
      return Object.values(groups).sort((a, b) => {
        return parseInt(a.order) - parseInt(b.order);
      });
    }
  },
  methods: {
    show() {
      this.loading = true;
      this.searchQuery = ""; // Reset search when opening
      this.selectedOrder = "all"; // Reset order filter
      this.$modal.show("LayersOrderModal");
      this.getLayersNoPages().then(() => {
        this.loading = false;
      }).catch(() => {
        this.loading = false;
      });
    },

    selectOrder(order) {
      this.$emit("selected", parseInt(order));
      this.$modal.hide("LayersOrderModal");
    },

    getCategoryName(catId) {
      if (!catId) return "Sin Categoría";
      const category = this.categories.find(c => c.id == catId);
      return category ? category.name : "Sin Categoría";
    },

    toggleGroup(order) {
      this.$set(this.collapsedGroups, order, !this.isCollapsed(order));
    },

    isCollapsed(order) {
      return !!this.collapsedGroups[order];
    }
  }
};
</script>

<style scoped>
/* Modal Container */
.premium-modal >>> .vm--modal {
  background: transparent !important; /* Remove library's default background */
  box-shadow: none !important; /* Remove library's default shadow if any */
  border: none !important;
}

.modal-content {
  background: #fff;
  display: flex;
  flex-direction: column;
  max-height: 90vh;
  width: 100%; /* Ensure it fills the modal container */
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
  border: 1px solid #eee;
  color: #2c3e50;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  overflow: hidden; /* Ensure rounded corners work */
}

/* Header */
.modal-header {
  padding: 20px 28px;
  border-bottom: 1px solid #f0f0f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #ffffff;
}

.modal-header h2 {
  margin: 0;
  font-size: 1.4rem;
  font-weight: 700;
  color: #1a1a1a;
  letter-spacing: -0.5px;
}

.close-button {
  background: #f5f5f7;
  border: none;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  font-size: 1.2rem;
  cursor: pointer;
  color: #666;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.close-button:hover {
  background: #e1e1e3;
  color: #000;
}

/* Body */
.modal-body {
  padding: 24px 28px;
  overflow-y: auto;
  background: #fafafa;
}

/* Filters */
.filters-container {
  margin-bottom: 24px;
  display: flex;
  gap: 20px;
  align-items: flex-end;
  flex-wrap: wrap;
}

.search-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 250px;
}

.search-icon {
  position: absolute;
  left: 14px;
  font-size: 1rem;
  color: #999;
}

.search-input {
  width: 100%;
  padding: 12px 15px 12px 42px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-size: 1rem;
  background: #fff;
  transition: all 0.3s;
  box-shadow: 0 2px 4px rgba(0,0,0,0.02);
}

.order-filter-wrapper {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.order-filter-wrapper label {
  font-size: 0.85rem;
  font-weight: 600;
  color: #7f8c8d;
}

.order-select {
  padding: 11px 15px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-size: 1rem;
  background: #fff;
  min-width: 180px;
  cursor: pointer;
  transition: all 0.3s;
}

.search-input:focus, .order-select:focus {
  border-color: #3c8dbc;
  outline: none;
  box-shadow: 0 0 0 3px rgba(60, 141, 188, 0.15);
}

/* Groups */
.order-group {
  margin-bottom: 12px;
  border: 1px solid #eaeaea;
  border-radius: 10px;
  background: #fff;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0,0,0,0.03);
  transition: transform 0.2s;
}

.order-group:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.05);
}

.group-header {
  padding: 16px 20px;
  background-color: #ffffff;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  user-select: none;
}

.group-header:hover {
  background-color: #f9fbff;
}

.group-header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.order-badge {
  background: #eef6ff;
  color: #3c8dbc;
  padding: 4px 10px;
  border-radius: 6px;
  font-weight: 700;
  font-size: 0.85rem;
}

.group-title {
  font-weight: 600;
  color: #333;
  font-size: 1rem;
}

.group-header-right {
  display: flex;
  align-items: center;
  gap: 15px;
}

.group-count {
  font-size: 0.85rem;
  color: #7f8c8d;
  font-weight: 500;
  background: #f1f3f5;
  padding: 2px 8px;
  border-radius: 10px;
}

.btn-select-order {
  padding: 6px 14px;
  background-color: #3c8dbc;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  opacity: 0.9;
}

.btn-select-order:hover {
  background-color: #367fa9;
  opacity: 1;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.btn-select-order:active {
  transform: translateY(0);
}

.collapse-icon {
  font-size: 0.8rem;
  color: #bdc3c7;
  transition: transform 0.3s;
}

.group-header.collapsed .collapse-icon {
  transform: scaleY(-1);
}

/* Tables */
.group-content {
  padding: 0 20px 16px 20px;
  border-top: 1px solid #f5f5f5;
}

.layers-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0 4px;
}

.layers-table th {
  padding: 12px 10px;
  text-align: left;
  color: #95a5a6;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.layers-table td {
  padding: 12px 10px;
  border-top: 1px solid #fcfcfc;
}

.layer-name {
  font-weight: 500;
  color: #2c3e50;
}

.category-tag {
  display: inline-block;
  padding: 2px 10px;
  background: #f8f9fa;
  border: 1px solid #eee;
  border-radius: 14px;
  font-size: 0.8rem;
  color: #666;
}

/* Footer */
.modal-footer {
  padding: 20px 28px;
  border-top: 1px solid #f0f0f0;
  display: flex;
  justify-content: flex-end;
  background: #ffffff;
}

.btn-close {
  padding: 10px 24px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  background-color: #34495e;
  border: none;
  color: white;
  transition: all 0.2s;
}

.btn-close:hover {
  background-color: #2c3e50;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0,0,0,0.1);
}

/* States */
.loading-state, .empty-state {
  text-align: center;
  padding: 60px 40px;
  color: #7f8c8d;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #3c8dbc;
  border-radius: 50%;
  margin: 0 auto 15px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Animations */
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s;
}
.fade-enter, .fade-leave-to {
  opacity: 0;
}
</style>
